import React, { useEffect, useRef } from 'react';

import { AuditTimelineEvent } from '../../../interfaces/Audit';
import '../planning-comparison/PlanningComparisonModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface SubtarefasDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: AuditTimelineEvent | null;
}

interface EsteiraNoDelta {
  esteira?: string | null;
  added?: string[];
  removed?: string[];
  preservadasPorConclusao?: string[];
}

/**
 * Detalhe de "Sub-tarefas do serviço definidas" (at-89, ADR 0041).
 *
 * Molde do `LiberacaoDetailsModal`: o card da timeline diz só o resumo, e o que mudou de fato —
 * quais sub-tarefas entraram e saíram, **por esteira** — vive aqui. Sem o modal, o `reason`
 * estruturado ficaria inacessível (é a armadilha registrada em `features/auditoria.md`: campo novo
 * no `reason` não aparece sozinho).
 *
 * ⚠️ Só `SET_SUBTAREFAS` abre modal. `SUBTAREFA_CONCLUIDA`/`REABERTA` são eventos de UM item, já
 * inteiramente descritos no título do card — modal ali seria um clique para reler a mesma frase.
 */
const SubtarefasDetailsModal: React.FC<SubtarefasDetailsModalProps> = ({
  isOpen,
  onClose,
  event,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleMouseDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const metadata = event.metadata || {};
  const porEsteira: EsteiraNoDelta[] = Array.isArray(metadata.porEsteira) ? metadata.porEsteira : [];

  const performer =
    event.collaboratorNames.length > 0
      ? event.collaboratorNames.join(', ')
      : metadata.performedByName || 'Sistema';

  const dateStr = new Date(event.occurredAt).toLocaleDateString('pt-BR');
  const timeStr = new Date(event.occurredAt).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={modalRef}>
        <div className="planning-modal-header">
          <h2>Sub-tarefas do serviço</h2>
          <button className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="planning-modal-metadata">
          <div className="metadata-item">
            <strong>Definidas por:</strong> <span>{performer}</span>
          </div>
          <div className="metadata-item">
            <strong>Data da alteração:</strong>{' '}
            <span>
              {dateStr} às {timeStr}
            </span>
          </div>
        </div>

        <div className="planning-modal-body">
          {porEsteira.length === 0 ? (
            <div className="planning-modal-fallback">
              <p className="fallback-title">{event.title}</p>
              <p className="fallback-desc">{event.description || 'Sem descrição complementar.'}</p>
              <p className="fallback-note">
                Este registro não contém o detalhamento por esteira.
              </p>
            </div>
          ) : (
            <div className="planning-comparison-grid single-column">
              {porEsteira.map((grupo, indexGrupo) => {
                const incluidas = grupo.added ?? [];
                const removidas = grupo.removed ?? [];
                const preservadas = grupo.preservadasPorConclusao ?? [];

                return (
                  <div className="planning-column" key={`grupo-${indexGrupo}`}>
                    <div className="column-header">{grupo.esteira || 'Esteira'}</div>
                    <div className="status-flow-list">
                      {incluidas.map((nome, index) => (
                        <div key={`add-${index}`} className="status-flow-item item-added">
                          <div className="item-index">{index + 1}</div>
                          <div className="item-label">{nome}</div>
                          <span className="badge-change font-added">Incluída</span>
                        </div>
                      ))}

                      {/* Numeração contínua entre as listas — a mesma correção do BUG-V58 na
                          Liberação: reiniciar em 1 exibiria "1, 2, 1, 2" numa lista única. */}
                      {removidas.map((nome, index) => (
                        <div key={`rem-${index}`} className="status-flow-item item-removed">
                          <div className="item-index">{incluidas.length + index + 1}</div>
                          <div className="item-label">{nome}</div>
                          <span className="badge-change font-removed">Removida</span>
                        </div>
                      ))}

                      {preservadas.map((nome, index) => (
                        <div key={`pres-${index}`} className="status-flow-item item-unchanged">
                          <div className="item-index">
                            {incluidas.length + removidas.length + index + 1}
                          </div>
                          <div className="item-label">{nome}</div>
                          <span className="badge-change">Mantida</span>
                        </div>
                      ))}
                    </div>

                    {preservadas.length > 0 && (
                      <div
                        style={{
                          marginTop: '16px',
                          padding: '12px 16px',
                          background: 'var(--tema-fundo, #f0fdf4)',
                          border: '1px solid var(--tema-borda-verde, #bbf7d0)',
                          borderRadius: '12px',
                        }}
                      >
                        <strong
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            color: 'var(--tema-texto-verde, #166534)',
                            fontFamily: 'AmpleSoft-Bold, sans-serif',
                            marginBottom: '6px',
                          }}
                        >
                          Mantidas porque já haviam sido concluídas
                        </strong>
                        <span style={{ fontSize: '12.5px', color: 'var(--tema-texto-verde, #15803d)' }}>
                          Estas sub-tarefas foram desmarcadas no planejamento, mas permanecem no
                          veículo: alguém já registrou tê-las feito, e esse histórico não é
                          apagado.
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubtarefasDetailsModal;
